import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { Sun, Moon, BookHalf } from "react-bootstrap-icons";
import { ResponsiveContainer, BarChart, Bar, Cell, Tooltip, CartesianGrid, XAxis, YAxis } from "recharts";

import { getAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import { getDefaultDateRange, getWidgetTheme } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, WidgetTheme, DocsSidebarEntry } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import { PopPeriodBanner } from "../../../components/analytics";
import { DocsSidebar, ExportButton, ScheduleExportModal, WidgetCard } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import HeatmapMetricsTable from "../../../components/analytics/HeatmapMetricsTable";
import ContentBrandTreeTable from "../../../components/analytics/ContentBrandTreeTable";
import Logger from "../../../utils/Logger";

type ContentDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ChartDataPoint = {
    name: string;
    value: number;
    fill: string;
};

type ChartCard = {
    key: string;
    docsKey: string;
    title: string;
    data: ChartDataPoint[];
};

type ContentGenreActionsRow = Record<string, any>;
type ContentGenreTableRow = Record<string, any>;
type ContentBrandTableRow = Record<string, any>;

type ContentDashboardState = {
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    isDarkTheme: boolean;
    genreActionsData: ChartDataPoint[];
    genreUsersData: ChartDataPoint[];
    shareContentData: ChartDataPoint[];
    shareBrandsData: ChartDataPoint[];
    genreTableData: ContentGenreTableRow[];
    brandTableData: ContentBrandTableRow[];
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    showScheduleModal: boolean;
    editingSchedule: null;
};


const chartColors = ["#007bff", "#20c997", "#fd7e14", "#6f42c1", "#e83e8c", "#17a2b8"];
const MAX_AXIS_LABEL_LENGTH = 30;

class ContentDashboard extends React.Component<ContentDashboardProps, ContentDashboardState> {
    constructor(props: ContentDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            devicePlatform: []
        };

        this.state = {
            isLoading: false,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                devicePlatforms: []
            },
            isDarkTheme: true,
            genreActionsData: [],
            genreUsersData: [],
            shareContentData: [],
            shareBrandsData: [],
            genreTableData: [],
            brandTableData: [],
            sidebarOpen: false,
            sidebarEntries: [],
            showScheduleModal: false,
            editingSchedule: null
        };
    }

    componentDidMount(): void {
        this.fetchDashboardData();
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters }
        }), () => {
            this.fetchDashboardData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { genreActionsData, genreUsersData, shareContentData, shareBrandsData, appliedFilters } = this.state;
        const data: ExportableData[] = [];

        if (genreActionsData.length > 0) {
            data.push({
                title: "Genre Share (Actions)",
                columns: [
                    { key: "name", label: "Genre" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: genreActionsData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (genreUsersData.length > 0) {
            data.push({
                title: "Genre Share (Users)",
                columns: [
                    { key: "name", label: "Genre" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: genreUsersData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (shareContentData.length > 0) {
            data.push({
                title: "Content Share",
                columns: [
                    { key: "name", label: "Content" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: shareContentData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (shareBrandsData.length > 0) {
            data.push({
                title: "Brand Share",
                columns: [
                    { key: "name", label: "Brand" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: shareBrandsData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Content Dashboard",
            pageKey: "content_dashboard",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => null
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private mapContentGenreActionsToChartData = (
        rows: ContentGenreActionsRow[],
        metric: "action_count" | "user_count"
    ): ChartDataPoint[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mapped = rows.map((row: ContentGenreActionsRow, index: number) => {
            const name = row.genre || `Segment ${index + 1}`;
            const value = Number(row[metric] ?? 0);
            return {
                name: String(name),
                value,
                fill: chartColors[index % chartColors.length]
            };
        }).filter((item: ChartDataPoint) => Number.isFinite(item.value) && item.value > 0);

        return mapped.slice(0, 20);
    };

    private mapContentGenreCatalogueToChartData = (
        rows: ContentGenreActionsRow[],
        metric: "unique_things" | "unique_brands"
    ): ChartDataPoint[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mapped = rows.map((row: ContentGenreActionsRow, index: number) => {
            const name = row.genre || `Segment ${index + 1}`;
            const value = Number(row[metric] ?? 0);
            return {
                name: String(name),
                value,
                fill: chartColors[index % chartColors.length]
            };
        }).filter((item: ChartDataPoint) => Number.isFinite(item.value) && item.value > 0);

        return mapped.slice(0, 20);
    };

    private fetchDashboardData = async (): Promise<void> => {
        const { appliedFilters } = this.state;

        try {
            this.setState({ isLoading: true, error: null });

            const [actionsResponse, catalogueResponse, genreTableResponse, brandTableResponse] = await Promise.all([
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "content_genre_actions",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "content_genre_catalogue",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "content_genre_table",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "content_brand_table",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                )
            ]);

            const result = (actionsResponse as any)?.result;
            const rows: ContentGenreActionsRow[] = Array.isArray(result) ? result : [];
            const actionCountData = this.mapContentGenreActionsToChartData(rows, "action_count");
            const userCountData = this.mapContentGenreActionsToChartData(rows, "user_count");

            const catalogueResult = (catalogueResponse as any)?.result;
            const catalogueRows: ContentGenreActionsRow[] = Array.isArray(catalogueResult) ? catalogueResult : [];
            const catalogueContentData = this.mapContentGenreCatalogueToChartData(catalogueRows, "unique_things");
            const catalogueBrandData = this.mapContentGenreCatalogueToChartData(catalogueRows, "unique_brands");

            const genreTableResult = (genreTableResponse as any)?.result;
            const genreTableData: ContentGenreTableRow[] = Array.isArray(genreTableResult) ? genreTableResult : [];

            const brandTableResult = (brandTableResponse as any)?.result;
            const brandTableData: ContentBrandTableRow[] = Array.isArray(brandTableResult) ? brandTableResult : [];

            this.setState({
                genreActionsData: actionCountData,
                genreUsersData: userCountData,
                shareContentData: catalogueContentData,
                shareBrandsData: catalogueBrandData,
                genreTableData,
                brandTableData
            });

            const toDisplayName = (value: string): string => value
                .replace(/_/g, " ")
                .replace(/\b\w/g, char => char.toUpperCase());
            const toSchemaFromDocs = (docs: unknown): Record<string, { description: string }> | undefined => {
                if (!docs || typeof docs !== "object") {
                    return undefined;
                }

                const entries = Object.entries(docs as Record<string, unknown>);
                if (entries.length === 0) {
                    return undefined;
                }

                return entries.reduce<Record<string, { description: string }>>((acc, [field, value]) => {
                    if (typeof value === "string") {
                        acc[field] = { description: value };
                        return acc;
                    }

                    if (value && typeof value === "object" && "description" in value) {
                        acc[field] = { description: String((value as { description?: unknown }).description ?? "") };
                        return acc;
                    }

                    acc[field] = { description: String(value ?? "") };
                    return acc;
                }, {});
            };
            const getSchemaForResponse = (response: unknown): Record<string, { description: string }> | undefined => {
                const schemaFromResponse = (response as any)?.schema as Record<string, { description: string }> | undefined;
                const schemaFromDocs = toSchemaFromDocs((response as any)?.docs);

                if (!schemaFromResponse && !schemaFromDocs) {
                    return undefined;
                }

                return {
                    ...(schemaFromResponse || {}),
                    ...(schemaFromDocs || {})
                };
            };
            const composeDescription = (
                response: unknown,
                schema: Record<string, { description: string }> | undefined,
                metricKey?: string,
                includeFieldDetails?: boolean
            ): string => {
                const fieldDetailsOnly = includeFieldDetails && schema
                    ? [
                        "### Field Definitions",
                        ...Object.entries(schema).map(([field, meta]) => `- \`${field}\`: ${meta.description}`)
                    ].join("\n")
                    : "";
                if (fieldDetailsOnly) {
                    return fieldDetailsOnly;
                }

                const metaDescription = String((response as any)?.widget?.description ?? "").trim();
                const metricDescription = metricKey && schema?.[metricKey]?.description
                    ? `${toDisplayName(metricKey)}: ${schema[metricKey].description}`
                    : "";
                const dimensionDoc = schema?.genre?.description
                    || schema?.thing_brandname?.description
                    || schema?.brand_name?.description
                    || "";
                const fieldsSummary = schema
                    ? `Available fields: ${Object.keys(schema).join(", ")}`
                    : "";

                return [metricDescription, metaDescription, dimensionDoc, fieldsSummary]
                    .filter(Boolean)
                    .join("\n\n");
            };

            const actionsSchema = getSchemaForResponse(actionsResponse);
            const catalogueSchema = getSchemaForResponse(catalogueResponse);
            const genreSchema = getSchemaForResponse(genreTableResponse);
            const brandSchema = getSchemaForResponse(brandTableResponse);

            const sidebarEntries: DocsSidebarEntry[] = [];
            const maybePushEntry = (entry: DocsSidebarEntry | null): void => {
                if (entry) {
                    sidebarEntries.push(entry);
                }
            };

            maybePushEntry((actionsSchema || (actionsResponse as any)?.widget) ? {
                widgetKey: "share_actions",
                displayName: "Share Of Actions",
                description: "Based on top 20 genres.\n\n" + composeDescription(actionsResponse, actionsSchema, "action_count", true),
                schema: undefined
            } : null);
            maybePushEntry((actionsSchema || (actionsResponse as any)?.widget) ? {
                widgetKey: "share_users",
                displayName: "Share Of Users",
                description: "Based on top 20 genres.\n\n" + composeDescription(actionsResponse, actionsSchema, "user_count", true),
                schema: undefined
            } : null);
            maybePushEntry((catalogueSchema || (catalogueResponse as any)?.widget) ? {
                widgetKey: "share_content",
                displayName: "Share Of Content",
                description: "Based on top 20 genres.\n\n" + composeDescription(catalogueResponse, catalogueSchema, "unique_things", true),
                schema: undefined
            } : null);
            maybePushEntry((catalogueSchema || (catalogueResponse as any)?.widget) ? {
                widgetKey: "share_brands",
                displayName: "Share Of Brands",
                description: "Based on top 20 genres.\n\n" + composeDescription(catalogueResponse, catalogueSchema, "unique_brands", true),
                schema: undefined
            } : null);
            maybePushEntry((genreSchema || (genreTableResponse as any)?.widget) ? {
                widgetKey: "content_genre_table",
                displayName: (genreTableResponse as any)?.widget?.display_name || toDisplayName("content_genre_table"),
                description: composeDescription(genreTableResponse, genreSchema, undefined, true),
                schema: undefined
            } : null);
            maybePushEntry((brandSchema || (brandTableResponse as any)?.widget) ? {
                widgetKey: "content_brand_table",
                displayName: (brandTableResponse as any)?.widget?.display_name || toDisplayName("content_brand_table"),
                description: composeDescription(brandTableResponse, brandSchema, undefined, true),
                schema: undefined
            } : null);
            this.setState({ sidebarEntries });
        } catch (error) {
            Logger.error("Error fetching content dashboard data", error);
            this.setState({ error: "Failed to load content genre actions" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private renderBarCard = (chart: ChartCard, theme: WidgetTheme): ReactNode => {
        const { sidebarEntries } = this.state;
        const sortedData = [...chart.data].sort((a, b) => b.value - a.value);
        const total = sortedData.reduce((sum, item) => sum + item.value, 0);
        const chartHeight = Math.max(280, sortedData.length * 24);

        const truncateAxisLabel = (label: string): string => {
            const normalizedLabel = label
                .replace(/[\r\n]+/g, " ")
                .replace(/\s+/g, " ")
                .trim();

            if (normalizedLabel.length <= MAX_AXIS_LABEL_LENGTH) {
                return normalizedLabel;
            }

            return `${normalizedLabel.slice(0, MAX_AXIS_LABEL_LENGTH - 1)}…`;
        };

        const renderYAxisTick = (props: any): ReactNode => {
            const { x, y, payload } = props;
            const formattedLabel = truncateAxisLabel(String(payload?.value ?? ""));

            return (
                <g transform={`translate(${x},${y})`}>
                    <text
                        x={-6}
                        y={0}
                        dy={3}
                        textAnchor="end"
                        fill={theme.text}
                        fontSize={11}
                    >
                        {formattedLabel}
                    </text>
                </g>
            );
        };

        return (
            <WidgetCard key={chart.key} title={chart.title} description={sidebarEntries.find(e => e.widgetKey === chart.docsKey)?.description} theme={theme} isEmpty={sortedData.length === 0}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        data={sortedData}
                        layout="vertical"
                        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                        <XAxis
                            type="number"
                            tick={{ fill: theme.text }}
                            axisLine={{ stroke: theme.border }}
                            tickLine={{ stroke: theme.border }}
                            tickFormatter={(value: number) => value.toLocaleString()}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={240}
                            interval={0}
                            tick={renderYAxisTick}
                            axisLine={{ stroke: theme.border }}
                            tickLine={{ stroke: theme.border }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: theme.chartBg,
                                border: `1px solid ${theme.border}`,
                                color: theme.text
                            }}
                            itemStyle={{ color: theme.text }}
                            labelStyle={{ color: theme.text }}
                            labelFormatter={(label: React.ReactNode) => `Genre: ${String(label ?? "")}`}
                            formatter={(value: number | string | Array<number | string> | undefined) => {
                                let numericValue = 0;

                                if (typeof value === "number") {
                                    numericValue = value;
                                } else if (typeof value === "string") {
                                    numericValue = Number(value) || 0;
                                } else if (Array.isArray(value)) {
                                    numericValue = Number(value[0]) || 0;
                                }

                                const percentage = total > 0 ? (numericValue / total) * 100 : 0;
                                return [`${numericValue.toLocaleString()} (${percentage.toFixed(1)}%)`, chart.title];
                            }}
                        />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {sortedData.map(entry => (
                                <Cell key={`${chart.key}-bar-${entry.name}`} fill={entry.fill} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </WidgetCard>
        );
    };

    private getChartsConfig = (): ChartCard[] => {
        const { genreActionsData, genreUsersData, shareContentData, shareBrandsData } = this.state;

        return [
            {
                key: "share_actions",
                docsKey: "share_actions",
                title: "Share of Actions",
                data: genreActionsData
            },
            {
                key: "share_users",
                docsKey: "share_users",
                title: "Share of Users",
                data: genreUsersData
            },
            {
                key: "share_content",
                docsKey: "share_content",
                title: "Share of Content",
                data: shareContentData
            },
            {
                key: "share_brands",
                docsKey: "share_brands",
                title: "Share of Brands",
                data: shareBrandsData
            }
        ];
    };

    private renderBrandTable = (theme: WidgetTheme): ReactNode => {
        const { brandTableData, sidebarEntries } = this.state;
        const isRevryCustomer = this.props.customerConfig.name.toLowerCase().includes("revry")
            || this.props.customerConfig.slug.toLowerCase().includes("revry");
        const hasHierarchyShape = brandTableData.some(row =>
            Object.prototype.hasOwnProperty.call(row, "channel_type")
            && Object.prototype.hasOwnProperty.call(row, "channel")
            && Object.prototype.hasOwnProperty.call(row, "user_country")
        );
        const useTreeTable = isRevryCustomer || hasHierarchyShape;
        return (
            <WidgetCard
                title="Brand Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_brand_table")?.description}
                theme={theme}
                isEmpty={brandTableData.length === 0}
                emptyMessage="No brand table data available for the selected date range."
            >
                {useTreeTable ? (
                    <ContentBrandTreeTable
                        data={brandTableData}
                        emptyMessage="No brand table data available"
                    />
                ) : (
                    <HeatmapMetricsTable
                        data={brandTableData}
                        emptyMessage="No brand table data available"
                        initialSortKey="plays"
                        preferredLeadingKeys={["thing_brandname", "brand_name", "brand"]}
                    />
                )}
            </WidgetCard>
        );
    };

    private renderGenreTable = (theme: WidgetTheme): ReactNode => {
        const { genreTableData, sidebarEntries } = this.state;
        return (
            <WidgetCard
                title="Genre Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_genre_table")?.description}
                theme={theme}
                isEmpty={genreTableData.length === 0}
                emptyMessage="No genre table data available for the selected date range."
            >
                <HeatmapMetricsTable
                    data={genreTableData}
                    emptyMessage="No genre table data available"
                    initialSortKey="plays"
                    preferredLeadingKeys={["genre", "genre_name", "name"]}
                />
            </WidgetCard>
        );
    };

    public render(): ReactNode {
        const { filters, filterOptions, appliedFilters, isDarkTheme, isLoading, error, sidebarOpen, sidebarEntries } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const charts = this.getChartsConfig();

        return (
            <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <Container className="mw-100" key="content-dashboard" style={{ position: "relative", paddingBottom: "40px" }}>
                <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                    <ExportButton
                        context={this.getExportContext()}
                        enabled={!isLoading && !error && (this.state.genreActionsData.length > 0 || this.state.genreUsersData.length > 0)}
                        scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                        onScheduleClick={() => this.setState({ showScheduleModal: true })}
                    />
                    <Button
                        onClick={this.toggleTheme}
                        style={{
                            backgroundColor: "transparent",
                            border: "none",
                            fontSize: "24px",
                            padding: "4px 8px",
                            color: isDarkTheme ? "#ffffff" : "#000000"
                        }}
                        title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                    >
                        {isDarkTheme ? <Sun /> : <Moon />}
                    </Button>
                    <Button
                        onClick={this.toggleSidebar}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                    >
                        <BookHalf />
                    </Button>
                </div>

                <Row>
                    <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                        <h2 style={{ color: theme.text }}>Content Dashboard</h2>
                    </Col>
                </Row>

                <AnalyticsFiltersComponent
                    filters={filters}
                    filterOptions={filterOptions}
                    onFilterChange={this.handleFilterChange}
                    onApplyFilters={this.handleApplyFilters}
                    customerConfig={this.props.customerConfig}
                    enableGroupBy={{}}
                />
                <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                {isLoading ? (
                    <Row className="justify-content-center">
                        <Col className="text-center" style={{ padding: "32px" }}>
                            <Spinner animation="border" variant="primary" />
                        </Col>
                    </Row>
                ) : error ? (
                    <Row>
                        <Col>
                            <Alert variant="danger" style={{ marginTop: "20px" }}>{error}</Alert>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row>
                            <Col md={12} style={{ marginTop: "20px" }}>
                                {this.renderBrandTable(theme)}
                            </Col>
                        </Row>
                        <Row>
                            {charts.map(chart => (
                                <Col key={chart.key} md={12} lg={6} style={{ marginTop: "20px" }}>
                                    {this.renderBarCard(chart, theme)}
                                </Col>
                            ))}
                        </Row>
                        <Row>
                            <Col md={12} style={{ marginTop: "20px" }}>
                                {this.renderGenreTable(theme)}
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
            </div>
            <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            <ScheduleExportModal
                show={this.state.showScheduleModal}
                onHide={() => this.setState({ showScheduleModal: false })}
                filters={this.state.appliedFilters}
                pageKey="content_dashboard"
                pageTitle="Content Dashboard"
                customerSlug={this.props.customerConfig.slug}
                editingSchedule={this.state.editingSchedule}
                onSave={this.handleScheduleSave}
            />
            </div>
        );
    }
}

export default ContentDashboard;
